// ResourceIcon — custom SVG icons for the four resource types
// Usage: <ResourceIcon type="metal" size={16} className="text-metal-400" />
// color is inherited via currentColor

interface Props {
  type: 'metal' | 'crystal' | 'deuterium' | 'energy'
  size?: number
  className?: string
}

function MetalIcon() {
  return (
    // Hexagonal ore chunk with internal grain lines
    <g>
      <polygon points="8,1.5 13.5,4.75 13.5,11.25 8,14.5 2.5,11.25 2.5,4.75" fill="currentColor" opacity="0.85" />
      <line x1="8" y1="1.5" x2="8" y2="14.5" stroke="white" strokeWidth="0.4" opacity="0.25" />
      <line x1="2.5" y1="4.75" x2="13.5" y2="11.25" stroke="white" strokeWidth="0.4" opacity="0.25" />
      <line x1="13.5" y1="4.75" x2="2.5" y2="11.25" stroke="white" strokeWidth="0.4" opacity="0.25" />
      <polygon points="8,4.5 11,6.25 11,9.75 8,11.5 5,9.75 5,6.25" fill="none" stroke="white" strokeWidth="0.5" opacity="0.3" />
    </g>
  )
}

function CrystalIcon() {
  return (
    // Diamond / gem cut shape
    <g>
      {/* Top crown facets */}
      <polygon points="8,1 13.5,6 8,15 2.5,6" fill="currentColor" opacity="0.9" />
      {/* Crown separation line */}
      <line x1="2.5" y1="6" x2="13.5" y2="6" stroke="white" strokeWidth="0.5" opacity="0.4" />
      {/* Crown left facet highlight */}
      <polygon points="8,1 2.5,6 5.75,6" fill="white" opacity="0.15" />
      {/* Pavilion center crease */}
      <line x1="8" y1="6" x2="8" y2="15" stroke="white" strokeWidth="0.4" opacity="0.2" />
      {/* Pavilion left */}
      <line x1="5" y1="6" x2="8" y2="15" stroke="white" strokeWidth="0.3" opacity="0.15" />
      {/* Pavilion right */}
      <line x1="11" y1="6" x2="8" y2="15" stroke="white" strokeWidth="0.3" opacity="0.15" />
    </g>
  )
}

function DeuteriumIcon() {
  return (
    // Stylized atom with orbital rings (deuterium = hydrogen isotope)
    <g>
      {/* Nucleus */}
      <circle cx="8" cy="8" r="2.2" fill="currentColor" />
      {/* Orbital ring 1 — horizontal ellipse */}
      <ellipse cx="8" cy="8" rx="6.5" ry="2" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.75" />
      {/* Orbital ring 2 — rotated 60° */}
      <ellipse cx="8" cy="8" rx="6.5" ry="2" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.75" transform="rotate(60 8 8)" />
      {/* Orbital ring 3 — rotated -60° */}
      <ellipse cx="8" cy="8" rx="6.5" ry="2" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.75" transform="rotate(-60 8 8)" />
    </g>
  )
}

function EnergyIcon() {
  return (
    // Lightning bolt
    <path
      d="M10,1.5 L4,8.5 H8.5 L6,14.5 L12,7.5 H7.5 L10,1.5 Z"
      fill="currentColor"
    />
  )
}

export function ResourceIcon({ type, size = 16, className = '' }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label={type}
      fill="none"
    >
      {type === 'metal'     && <MetalIcon />}
      {type === 'crystal'   && <CrystalIcon />}
      {type === 'deuterium' && <DeuteriumIcon />}
      {type === 'energy'    && <EnergyIcon />}
    </svg>
  )
}
